<template>
	<div>
		<slot name="label">
			<div v-if="label" class="tw-pb-1.5 tw-font-medium tw-text-gray-600">
				<span>{{ label }}</span>
				<span v-if="props.rules.includes('required')" class="tw-text-error-400">*</span>
			</div>
		</slot>

		<div class="tw-space-y-3">
			<div
				v-for="(item, index) in inputModel"
				:key="index"
				class="tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-gray-300 tw-p-3"
			>
				<div class="tw-flex-1">
					<AppInput
						v-model="item[nameKey]"
						size="sm"
						:disabled="disabled"
						:autoCompleteOptions="nameAutoCompleteList || []"
					/>
				</div>
				<div class="tw-flex-1">
					<AppInput
						v-model="item[valueKey]"
						size="sm"
						:disabled="disabled"
						:autoCompleteOptions="valueAutoCompleteList?.[item[nameKey]] || []"
					/>
				</div>
				<AppButton
					v-if="!disabled"
					startIcon="trash"
					variant="error"
					size="sm"
					square
					secondary
					type="button"
					@click="removeItem(index)"
					class="tw-mt-0.5"
				/>
			</div>

			<AppButton
				v-if="!disabled"
				:label="addButtonLabel"
				startIcon="plus"
				type="button"
				tertiary
				size="sm"
				@click="addItem"
			/>
		</div>

		<div
			:class="rules.length || hint || allocateBottomSpace ? 'tw-pt-1 tw-text-sm' : ''"
			:style="rules.length || hint || allocateBottomSpace ? 'min-height: 20px' : ''"
		>
			<div v-if="!errorMessage" class="tw-text-gray-600">
				<slot name="hint">
					{{ hint }}
				</slot>
			</div>
			<div v-if="errorMessage" class="tw-text-error-500">
				{{ errorMessage }}
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import { useFieldValidation } from '@/v2/utils/forms';
import { t } from '@/locales';

const props = defineProps({
	modelValue: {
		type: Array,
		default: () => [],
	},
	nameKey: {
		type: String,
		default: 'name',
	},
	valueKey: {
		type: String,
		default: 'value',
	},
	label: {
		type: String,
		default: '',
	},
	hint: {
		type: String,
		default: '',
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	rules: {
		type: Array,
		default: () => [],
	},
	allocateBottomSpace: {
		type: Boolean,
		default: false,
	},
	addButtonLabel: {
		type: String,
		default: () => t('real-estate.add-item'),
	},
	nameAutoCompleteList: {
		type: Array,
		default: () => [],
	},
	valueAutoCompleteList: {
		type: Object,
		default: () => ({}),
	},
});

const emit = defineEmits(['update:modelValue']);

const inputModel = computed({
	get() {
		const value = props.modelValue || [];
		if (value.length === 0) {
			return [{ [props.nameKey]: '', [props.valueKey]: '' }];
		}
		return value;
	},
	set(value) {
		emit('update:modelValue', value);
	},
});

const addItem = () => {
	const newItems = [...inputModel.value, { [props.nameKey]: '', [props.valueKey]: '' }];
	inputModel.value = newItems;
};

const removeItem = (index) => {
	const newItems = [...inputModel.value];
	newItems.splice(index, 1);
	if (newItems.length === 0) {
		newItems.push({ [props.nameKey]: '', [props.valueKey]: '' });
	}
	inputModel.value = newItems;
};

const { errorMessage } = useFieldValidation(
	() => props.modelValue,
	() => props.rules,
);
</script>
